import { useEffect, useRef, type RefObject } from "react";

type Mode =
  /** 0 → 1 while a tall section scrolls past a sticky child (pinned scenes) */
  | { kind: "pin" }
  /** 0 → 1 while the element's top travels from `from` to `to` (fractions of the viewport height) */
  | { kind: "travel"; from: number; to: number };

/**
 * Scroll-linked progress for Apple-style scenes. Writes the value to the
 * element's `--p` CSS variable on every frame (no React re-render), and
 * optionally reports it to `onProgress` for logic that needs it in JS.
 */
export function useScrollProgress<T extends HTMLElement>(
  mode: Mode,
  onProgress?: (p: number) => void,
): RefObject<T> {
  const ref = useRef<T>(null);
  const cb = useRef(onProgress);
  cb.current = onProgress;
  const modeKey = JSON.stringify(mode);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window === "undefined") return;
    const m = JSON.parse(modeKey) as Mode;
    let frame = 0;
    let last = -1;

    const update = () => {
      frame = 0;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      let p: number;
      if (m.kind === "pin") {
        const span = r.height - vh;
        p = span > 0 ? -r.top / span : r.top < 0 ? 1 : 0;
      } else {
        const start = vh * m.from;
        const end = vh * m.to;
        p = (start - r.top) / (start - end);
      }
      p = Math.min(1, Math.max(0, p));
      if (Math.abs(p - last) < 0.0005) return;
      last = p;
      el.style.setProperty("--p", p.toFixed(4));
      cb.current?.(p);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [modeKey]);

  return ref;
}
